<template>
  <div class="qty-wrapper">
    <!-- Title -->
    <div class="qty-label">QTY</div>
    
    <!-- Control buttons -->
    <div class="qty-control">
      <!-- Minus Button -->
      <button 
        class="qty-btn qty-btn--minus"
        @click="decreaseQty"
        :disabled="localQuantity <= minQty"
        type="button"
      >
        −
      </button>
      
      <!-- Input Field -->
      <input
        :id="`size-input-single-${productId}`"
        v-model.number="localQuantity"
        type="number"
        class="qty-input size-row__input--add-to-bundle"
        :min="minQty"
        :step="qtyIncrement"
        @input="handleInput"
        @blur="handleBlur"
      />
      
      <!-- Plus Button -->
      <button 
        class="qty-btn qty-btn--plus"
        @click="increaseQty"
        type="button"
      >
        +
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
  productId: {
    type: Number,
    required: true
  },
  modelValue: {
    type: Number,
    default: 25
  },
  minQty: {
    type: Number,
    default: 25
  },
  qtyIncrement: {
    type: Number,
    default: 25
  }
});

const emit = defineEmits(['update:modelValue', 'update:quantity']);

const localQuantity = ref(props.modelValue);

// Watch for external changes (from parent)
watch(() => props.modelValue, (newValue) => {
  localQuantity.value = newValue;
});

const handleInput = () => {
  // Allow any input while typing
  emit('update:modelValue', localQuantity.value);
  emit('update:quantity', localQuantity.value);
};

const handleBlur = () => {
  // Validate and adjust on blur
  if (localQuantity.value < props.minQty) {
    localQuantity.value = props.minQty;
  } else {
    // Round to nearest increment
    const remainder = localQuantity.value % props.qtyIncrement;
    if (remainder !== 0) {
      localQuantity.value = Math.round(localQuantity.value / props.qtyIncrement) * props.qtyIncrement;
    }
  }
  
  // Ensure we don't go below minimum
  if (localQuantity.value < props.minQty) {
    localQuantity.value = props.minQty;
  }
  
  emit('update:modelValue', localQuantity.value);
  emit('update:quantity', localQuantity.value);
  
  console.log('[CalendarQuantityInput] Quantity adjusted on blur:', localQuantity.value);
};

const decreaseQty = () => {
  if (localQuantity.value > props.minQty) {
    localQuantity.value -= props.qtyIncrement;
    if (localQuantity.value < props.minQty) {
      localQuantity.value = props.minQty;
    }
    emit('update:modelValue', localQuantity.value);
    emit('update:quantity', localQuantity.value);
  }
};

const increaseQty = () => {
  localQuantity.value += props.qtyIncrement;
  emit('update:modelValue', localQuantity.value);
  emit('update:quantity', localQuantity.value);
};

onMounted(() => {
  console.log('[CalendarQuantityInput] Mounted with productId:', props.productId);
  console.log('[CalendarQuantityInput] Input ID:', `size-input-single-${props.productId}`);
});
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;
.qty-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qty-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 35px;
}

.qty-control {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.qty-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: #5a5a5a;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.qty-btn:hover:not(:disabled) {
  background: #5a5a5a;
}

.qty-btn:active:not(:disabled) {
  background:#5a5a5a;
}

.qty-btn:disabled {
  //opacity: 0.4;
  cursor: not-allowed;


  color: #fff;
}

.qty-btn--minus {
  border-right: 1px solid #ddd;
}

.qty-btn--plus {
  border-left: 1px solid #ddd;
}

.qty-wrapper .qty-input {
  width: 85px;
  height: 30px;
  min-height: 30px;
  margin: 0;
  padding: 0 8px;
  font-size: 14px;
  font-family: $font-family-base;
  font-weight: 400;
  text-align: center;
  border: none;
  background: white;
  color: #333;
}

.qty-input:focus {
  outline: none;
  background: #fafafa;
}

/* Remove number input arrows */
.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input[type=number] {
  -moz-appearance: textfield;
}
</style>
